import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Card, Form } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { FileUp, LoaderCircle } from 'lucide-react';

import ConsentDialog from '@/components/ConsentDialog';
import ProgramForm from '@/components/ProgramForm';
import { programLabels } from '@/config';
import { getManufacturerByProgram } from '@/libs/util';
import useGlobalConfig from '@/store/global';

import CentralBillingOnly from './programs/components/CentralBillingOnly';
import ReimbursementAddress from './programs/components/ReimbursementAddress';

const needConsentPrograms = ['wilate', 'nuwiq', 'cutaquig'];

const PDFJS = (window as any).pdfjsLib;

const AssistanceRequestFormByUpload = () => {
  const themeColor = localStorage.getItem('themeColor');
  const { program } = useGlobalConfig();
  const [form] = Form.useForm();
  const [extractLoading, setExtractLoading] = useState(false);
  const [icdCodeNames, setIcdCodeNames] = useState([]);
  const [meData, setMeData] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [extractError, setExtractError] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);

  const consentDialogRef = useRef<any>(null);
  const isOctaPharma = getManufacturerByProgram(program.name)?.indexOf('Octapharma') !== -1;
  const renderConsentDialog = needConsentPrograms.includes(program.name);

  const loadData = async () => {
    const { data } = await axios.get('programs/icd_code_names');
    setIcdCodeNames(data);
  };

  const loadMeData = async () => {
    const { data } = await axios.get('sessions/me');
    setMeData(data);
  };

  let renderRef = useRef(false);

  const extractInfo = async (base64_image: any) => {
    try {
      const result: any = await axios.post('extract_info', {
        base64_image,
      });

      const {
        patient_first_name,
        patient_last_name,
        patient_state,
        patient_sex: patient_gender,
        patient_phone_number,
        patient_city,
        patient_birth_date,
        priscriper_first_name,
        priscriper_last_name,
        insurance_plan_name: primary_payer,
        provider_npi: prescribing_physician_npi,
        patient_zip_code: patient_zip,
        patient_street_address,
      } = result.form_fields;

      form.setFieldsValue({
        patient_first_name,
        patient_last_name,
        patient_state,
        patient_gender_code: patient_gender === 'Male' ? 1 : 2,
        patient_phone_number: patient_phone_number.replace(/\D/g, ''),
        patient_city_address: patient_city,
        patient_street_address,
        patient_zip,
        date_of_birth: dayjs(patient_birth_date),
        prescribing_physician_name: `${priscriper_first_name} ${priscriper_last_name}`,
        primary_payer,
        prescribing_physician_npi,
      });
      setShowRequestForm(true);
    } catch (e: any) {
      setExtractError(e.message);
    } finally {
      setExtractLoading(false);
    }
  };

  const renderPage = async (pdfDoc: any) => {
    const imagesList = [];
    const canvas = document.createElement('canvas');
    canvas.setAttribute('className', 'canv');

    // Get device pixel ratio
    const pixelRatio = window.devicePixelRatio || 1;

    const SCALE = 1.0 * pixelRatio;

    for (let i = 1; i <= pdfDoc.numPages; i++) {
      const page = await pdfDoc.getPage(i);
      const viewport = page.getViewport({ scale: SCALE });

      // Set canvas dimensions with pixel ratio
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Get canvas context and set rendering quality
      const ctx = canvas.getContext('2d', { alpha: false }) as CanvasRenderingContext2D;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const render_context = {
        canvasContext: ctx,
        viewport: viewport,
        enableWebGL: true,
        renderInteractiveForms: true,
      };

      await page.render(render_context).promise;

      // Convert to high-quality PNG
      const img = canvas.toDataURL('image/png', 1.0);
      imagesList.push(img);
    }
    await extractInfo(imagesList[0]);
  };

  useEffect(() => {
    if (renderRef.current) {
      renderRef.current = false;
      return;
    }
    loadData();
    loadMeData();
  }, []);

  const handleSubmitConsent = async () => {
    const values = await form.validateFields();
    try {
      setLoading(true);
      const file = fileRef.current?.files?.[0];
      const { data } = await axios.post(`assistance_requests`, {
        ...values,
        help_consent: true,
      });

      if (file) {
        const formData = new FormData();
        formData.append('hicf_file', file);

        await axios.post(`assistance_requests/${data.id}/upload_hicf`, formData, {
          headers: {
            Authorization: `${localStorage.getItem('Authorization')}`,
            ProgramName: 'wilate',
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      console.log('response: ', data);
      navigate(-1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const file = fileRef.current?.files?.[0];

      const { data } = await axios.post(`assistance_requests`, values);

      if (file) {
        const formData = new FormData();
        formData.append('hicf_file', file);

        await axios.post(`assistance_requests/${data.id}/upload_hicf`, formData, {
          headers: {
            Authorization: `${localStorage.getItem('Authorization')}`,
            ProgramName: 'wilate',
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      navigate(-1);
    } catch (e: any) {
      const errorMessage = e?.data?.message?.split('\n')?.[0] || e?.data?.message;
      if (errorMessage?.includes('Patient must consent') && isOctaPharma) {
        if (needConsentPrograms.includes(program.name)) {
          consentDialogRef.current?.open();
        }
      }
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async () => {
    const file = fileRef.current?.files?.[0];
    if (file) {
      setExtractLoading(true);
      const uri = URL.createObjectURL(file);
      const pdfFile = await PDFJS.getDocument({ url: uri });
      await renderPage(pdfFile);
    }
  };

  return (
    <>
      <div>
        <div className="max-w-screen-xl mx-auto">
          <h1 className="mb-4 text-xl md:text-2xl font-bold">
            {programLabels[program.name].title}
          </h1>
          <div className="my-10" hidden={showRequestForm || extractLoading}>
            <div className="h-[340px]  bg-zinc-50 hover:bg-zinc-200 duration-200 border rounded-md">
              <div className="h-full flex flex-col items-center justify-center gap-y-4">
                <button
                  className="w-full h-full flex flex-col items-center justify-center gap-y-4"
                  onClick={() => fileRef.current?.click()}
                >
                  <FileUp className="w-8 h-8" />
                  Select a CMS 1500 form (Only PDF file)
                </button>
                <input
                  ref={fileRef}
                  onChange={handleFileSelect}
                  hidden
                  type="file"
                  accept="application/pdf"
                />
              </div>
            </div>
            {extractError && (
              <p className="text-red-500 text-sm mt-2 text-center">
                Extract information failed: {extractError}
              </p>
            )}
          </div>
          {extractLoading && (
            <Card className="my-10 bg-zinc-50">
              <div className="text-sm text-zinc-500 flex items-center gap-x-2">
                <LoaderCircle className="animate-spin" />
                Analyzing the CMS 1500 form...
              </div>
              <div className="text-sm text-zinc-500 grid grid-cols-2 gap-6 mt-10 animate-pulse">
                <div>
                  <div className="w-20 h-3 mb-2 bg-zinc-300 rounded"></div>
                  <div className="w-full h-10 bg-zinc-200 border rounded-md"></div>
                </div>
                <div>
                  <div className="w-20 h-3 mb-2 bg-zinc-300 rounded"></div>
                  <div className="w-full h-10 bg-zinc-200 border rounded-md"></div>
                </div>
                <div>
                  <div className="w-20 h-3 mb-2 bg-zinc-300 rounded"></div>
                  <div className="w-full h-10 bg-zinc-200 border rounded-md"></div>
                </div>
                <div>
                  <div className="w-20 h-3 mb-2 bg-zinc-300 rounded"></div>
                  <div className="w-full h-10 bg-zinc-200 border rounded-md"></div>
                </div>
                <div>
                  <div className="w-20 h-3 mb-2 bg-zinc-300 rounded"></div>
                  <div className="w-full h-10 bg-zinc-200 border rounded-md"></div>
                </div>
                <div>
                  <div className="w-20 h-3 mb-2 bg-zinc-300 rounded"></div>
                  <div className="w-full h-10 bg-zinc-200 border rounded-md"></div>
                </div>
              </div>
            </Card>
          )}
          {showRequestForm && (
            <div className="my-10 octapharma-form">
              <Form form={form} onFinish={handleSubmit} layout="vertical">
                <Card className="mb-6 bg-zinc-50">
                  <ProgramForm
                    programName={program.name}
                    icdCodeNames={icdCodeNames}
                    meData={meData}
                  />
                </Card>

                <CentralBillingOnly meData={meData} />

                <ReimbursementAddress meData={meData} />

                <Form.Item
                  wrapperCol={{
                    offset: 8,
                    span: 14,
                  }}
                >
                  <Button
                    loading={loading}
                    type="primary"
                    htmlType="submit"
                    disabled={loading}
                    style={{
                      width: '60%',
                      backgroundColor: themeColor as string,
                      height: '40px',
                      fontSize: '18px',
                      marginTop: '30px',
                    }}
                  >
                    Submit
                  </Button>
                </Form.Item>
              </Form>
            </div>
          )}
        </div>
      </div>
      {renderConsentDialog && <ConsentDialog ref={consentDialogRef} submit={handleSubmitConsent} />}
    </>
  );
};

export default AssistanceRequestFormByUpload;
